import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import { inject, observer } from 'mobx-react'
import { Formik, Form, Field } from 'formik'
import { toJS } from 'mobx'
import pick from 'lodash/pick'
import fromPairs from 'lodash/fromPairs'
import WarningIcon from '@components/Icon/Warning'
import InfoBox from '@components/InfoBox'
import Input from '@components/Form/Input'
import { Dropdown } from '@components/Form/Autocomplete'
import Button from '@components/Button'
import OptionGroup from '@components/Form/OptionGroup'
import yup from 'yup'
import EmployeeFormCommon from '../EmployeeForm/EmployeeForm.common'
import moment from 'moment'

class PositionForm extends Component {
  static propTypes = {
    position: PropTypes.object,
    employeePermissions: PropTypes.array,
    modalStore: PropTypes.object,
    staticData: PropTypes.object,
    employeeStore: PropTypes.object,
    growlStore: PropTypes.object
  }

  savePosition = positionData => {
    const { employeeStore, position } = this.props

    return employeeStore.updatePosition({ id: position.id, ...positionData })
  }

  handlePositionChange = (positionId, form) => {
    const permissions = EmployeeFormCommon.getDefaultPermissionsByPosition(
      positionId,
      this.props.staticData
    )
    form.setFieldValue('employeePositionId', positionId, false)
    form.setFieldValue('permissions', permissions, false)
    form.setFieldTouched('employeePositionId', true, true)
    form.setFieldTouched('permissions', true, true)
  }

  handleSubmit = (values, actions) => {
    const { modalStore, employeeStore, growlStore } = this.props

    this.savePosition(values)
      .then(() => {
        employeeStore.get(employeeStore.employee.id)
        growlStore.showSuccess('Position updated')
        modalStore.close()
      })
      .catch(({ message }) => {
        this.error = message
        growlStore.showError(message)
        actions.setSubmitting(false)
      })
  }

  getPositionValidationSchema () {
    return yup.object().shape({
      employeePositionId: yup
        .number()
        .nullable()
        .required('Employee position field is required'),
      positionDescription: yup
        .string()
        .nullable()
        .required('Position description field is required'),
      hourlyRate: yup
        .number()
        .positive('Hourly rate amount must be a positive number')
        .typeError('Hourly rate amount must be a number')
        .required('Hourly rate field is required.'),
      currencyId: yup
        .number()
        .nullable()
        .required('Currency field is required')
    })
  }

  handleAmountChange = (amount, form) => {
    if (amount === '') {
      form.setFieldValue('hourlyRate', null, false)
    } else {
      form.setFieldValue('hourlyRate', Number(amount), false)
    }

    form.setFieldTouched('hourlyRate', true, true)
  }

  renderPermissions = position => {
    const { staticData } = this.props
    const { permissions } = staticData

    if (position.effectiveDate !== moment().format('YYYY-MM-00')) {
      return null
    }

    const permissionSet = permissions.filter(perm => {
      const { PERMISSIONS } = staticData.CONST

      return ![
        PERMISSIONS.BASIC_INTERNAL,
        PERMISSIONS.BASIC_CLIENT,
        PERMISSIONS.BASIC_EXPERT
      ].includes(perm.id)
    })

    const permissionNames = fromPairs(
      permissionSet.map(perm => [perm.id, perm.prettyName])
    )

    const permissionItems = permissionSet.map(perm => perm.id)

    return (
      <div className='expert-form__section'>
        <h2 className='expert-form__subtitle'>Permissions</h2>
        <Field
          name='permissions'
          items={permissionItems}
          itemToString={item => permissionNames[item]}
          noStyle
          renderItem={EmployeeFormCommon.PermissionCheckbox}
          component={OptionGroup}
        />
      </div>
    )
  }

  render () {
    const { position, modalStore, staticData, employeePermissions } = this.props
    const { currencies, employeePositions } = staticData

    employeePositions.sort((p1, p2) => (p1.name > p2.name ? 1 : -1))

    return (
      <div>
        <Formik
          enableReinitialize
          initialValues={initializeForm({ position, employeePermissions })}
          validationSchema={this.getPositionValidationSchema()}
          onSubmit={this.handleSubmit}
          render={form => (
            <div className='expert-form'>
              <h1 className='expert-form__title'>{'Edit position'}</h1>
              {this.error ? (
                <InfoBox theme='red' icon={<WarningIcon />}>
                  {this.error}
                </InfoBox>
              ) : null}
              {this.warning ? (
                <InfoBox theme='yellow' icon={<WarningIcon />}>
                  {this.warning}
                </InfoBox>
              ) : null}
              <Form onSubmit={form.handleSubmit}>
                <div className='expert-form__section'>
                  <Field
                    name='employeePositionId'
                    label='Employee position'
                    placeholder='Select employee position'
                    itemToString={item => item && item.name}
                    options={employeePositions}
                    onChange={position =>
                      this.handlePositionChange(position.id, form)
                    }
                    component={Dropdown}
                  />
                  <Field
                    name='positionDescription'
                    label='Position description'
                    component={Input}
                  />
                  <div className='expert-form__input-group'>
                    <label className='expert-form__label'>Hourly rate</label>
                    <div className='expert-form__input-wrapper expert-form__input-wrapper--two_one_children'>
                      <Field
                        name='currencyId'
                        placeholder='Currency'
                        itemToString={item => item && item.name}
                        options={currencies}
                        component={Dropdown}
                      />
                      <Field
                        name='hourlyRate'
                        placeholder='Amount'
                        onChange={e =>
                          this.handleAmountChange(e.target.value, form)
                        }
                        component={Input}
                      />
                    </div>
                  </div>
                </div>
                {this.renderPermissions(position)}
                <div className='expert-form__section p-b-0'>
                  <Button
                    primary
                    submit
                    onClick={this.onSubmit}
                    disabled={form.isSubmitting}
                  >
                    Save
                  </Button>
                  <Button onClick={modalStore.close}>Cancel</Button>
                </div>
              </Form>
            </div>
          )}
        />
      </div>
    )
  }
}

PositionForm.defaultProps = {
  hideButtons: true,
  showCloseIcon: true,
  shouldCloseOnOverlayClick: false
}

export default withRouter(
  inject(
    'modalStore',
    'staticData',
    'growlStore',
    'employeeStore'
  )(observer(PositionForm))
)

function initializeForm ({ position, employeePermissions }) {
  if (position) return getInitialPositionData(position, employeePermissions)

  return {
    employeePositionId: null,
    positionDescription: '',
    hourlyRate: null,
    currencyId: null,
    permissions: []
  }
}

function getInitialPositionData (position, employeePermissions) {
  let positionData = pick(toJS(position), [
    'employeePositionId',
    'positionDescription',
    'hourlyRate',
    'currencyId'
  ])

  // converting due to the db query fetching in 'raw' mode so the model getter is not invoked
  positionData.hourlyRate /= 1000000
  positionData.permissions = employeePermissions

  return positionData
}
